/**
 * App backdrop painters: "Woodland dapple" (day) and "Night mist" (night).
 *
 * Every tunable lives in BACKDROP. Sizes are fractions of the viewport so the
 * look holds from a phone to a desktop; randomness is seeded so the layout is
 * identical on every load.
 *
 * Cost model: both canvases are pure soft light, so they render at a fraction
 * of CSS resolution and are upscaled by the compositor. A day frame is a clear
 * plus ~30 drawImage calls of two pre-rendered sprites; a night frame is a clear
 * plus ~8 drawImage calls of a tiny canvas. Each loop is throttled to its `fps`
 * and runs only while its scene is visible. Night grain is a static CSS tile
 * (dithers the dark gradients so they never band) generated once.
 */

export type Scene = 'day' | 'night';
type RGB = readonly [number, number, number];
type Range = readonly [number, number];

interface Blob {
  color: RGB;
  /** Peak opacity at the blob's centre. */
  alpha: number;
  /** Centre as fractions of the viewport (may sit off-screen). */
  x: number;
  y: number;
  /** Radius as a fraction of the viewport's geometric mean side, √(w·h). */
  r: number;
}

export const BACKDROP = {
  /** Cross-fade when the effective theme flips (night mode toggles live). */
  fadeMs: 600,

  day: {
    ground: 'radial-gradient(70% 45% at 18% 0%, rgb(255 246 214 / 0.7), transparent 70%), linear-gradient(160deg, #e7eedf 0%, #dfe8d6 60%, #eef0e6 100%)',
    seed: 7,
    /** Canvas pixels per CSS pixel. The layer is pure blur, so 0.5 is visually lossless. */
    resolution: 0.5,
    fps: 15,
    /** Blobs per (short side)² of viewport, clamped to `count`. */
    density: 13,
    count: [12, 44] as Range,
    /** Blob radius as a fraction of the viewport's short side. */
    radius: [0.07, 0.3] as Range,
    goldShare: 0.45,
    gold: [255, 232, 160] as RGB,
    green: [150, 190, 120] as RGB,
    greenAlpha: 0.8,
    /** Base opacity, and the slow "breathing" added on top of it. */
    alpha: 0.3,
    pulse: 0.12,
    /** Period of the breathing, ms. */
    pulseMs: 16000,
    /** Horizontal sway in CSS px, and its period in ms (leaves moving in a breeze). */
    sway: 6,
    swayMs: 25000,
    /** Small bright flecks of direct sun, on top of the blobs. */
    flecks: 10,
    fleckRadius: [0.018, 0.04] as Range,
    fleckAlpha: 0.45,
  },

  night: {
    /** Ground under the blobs; its top colour is the PWA theme-color (#0c1719). */
    ground: 'linear-gradient(180deg, #0c1719 0%, #0a1416 45%, #080f12 100%)',
    seed: 9,
    /** Canvas pixels per CSS pixel. Everything is a huge soft blob, so 0.25 is plenty. */
    resolution: 0.25,
    fps: 8,
    /** Each blob wanders on a slow Lissajous path: amplitude (fraction of √(w·h)) and period range, ms. */
    drift: 0.07,
    driftMs: [70000, 140000] as Range,
    /** Opacity breathing on top of each blob's alpha (fraction of it). */
    breathe: 0.15,
    blobs: [
      { color: [24, 78, 64], alpha: 0.55, x: 0.08, y: 0.22, r: 0.62 }, // forest green, upper left
      { color: [16, 72, 80], alpha: 0.42, x: 0.92, y: 0.42, r: 0.58 }, // teal, right
      { color: [22, 36, 82], alpha: 0.45, x: 0.3, y: 0.82, r: 0.7 }, // midnight blue, low
      { color: [18, 62, 52], alpha: 0.45, x: 0.85, y: 0.95, r: 0.5 }, // deep green, lower right
      { color: [30, 58, 96], alpha: 0.2, x: 0.55, y: 0.5, r: 0.45 }, // blue haze, centre
      { color: [14, 52, 58], alpha: 0.4, x: 0.15, y: 0.6, r: 0.4 }, // teal, mid left
    ] as Blob[],
    /** Faint cool moon glow near the top. */
    moon: { color: [196, 218, 232], alpha: 0.16, x: 0.7, y: -0.04, r: 0.42 } as Blob,
    /** Film grain over the night layer: per-pixel opacity ceiling and tile size (CSS px; drawn at device resolution). */
    grain: 0.035,
    grainTile: 128,
  },
};

export type BackdropConfig = typeof BACKDROP;

/** Park-Miller LCG: tiny, deterministic, good enough for scenery. */
function rng(seed: number): () => number {
  let s = seed % 2147483647 || 1;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}
const lerp = (r: Range, t: number): number => r[0] + (r[1] - r[0]) * t;

/* ------------------------------------------------------------------ day */

function sprite(rgb: RGB): HTMLCanvasElement {
  const n = 128;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
  const [r, gr, b] = rgb;
  grd.addColorStop(0, `rgb(${r} ${gr} ${b} / 1)`);
  grd.addColorStop(0.45, `rgb(${r} ${gr} ${b} / 0.62)`);
  grd.addColorStop(1, `rgb(${r} ${gr} ${b} / 0)`);
  g.fillStyle = grd;
  g.fillRect(0, 0, n, n);
  return c;
}

interface Spot {
  x: number;
  y: number;
  s: number;
  gold: boolean;
  p: number;
  fleck: boolean;
}

export class Dapple {
  private g: CanvasRenderingContext2D;
  private spots: Spot[] = [];
  private gold: HTMLCanvasElement;
  private green: HTMLCanvasElement;
  private w = 0;
  private h = 0;

  constructor(
    private canvas: HTMLCanvasElement,
    private cfg: BackdropConfig['day'] = BACKDROP.day,
  ) {
    this.g = canvas.getContext('2d', { alpha: true })!;
    this.gold = sprite(cfg.gold);
    this.green = sprite(cfg.green);
  }

  resize(w: number, h: number): void {
    const c = this.cfg;
    this.w = w;
    this.h = h;
    this.canvas.width = Math.max(1, Math.round(w * c.resolution));
    this.canvas.height = Math.max(1, Math.round(h * c.resolution));
    this.g.setTransform(c.resolution, 0, 0, c.resolution, 0, 0);
    // Same seed every time: the first N spots are stable, so a resize scales the
    // layout instead of reshuffling it.
    const r = rng(c.seed);
    const min = Math.min(w, h);
    const n = Math.round(Math.min(c.count[1], Math.max(c.count[0], (c.density * w * h) / (min * min))));
    this.spots = [];
    for (let i = 0; i < c.count[1] + c.flecks; i++) {
      const fleck = i >= c.count[1];
      const spot = {
        x: r() * w,
        y: r() * h,
        s: lerp(fleck ? c.fleckRadius : c.radius, r()) * min,
        gold: fleck || r() < c.goldShare,
        p: r() * Math.PI * 2,
        fleck,
      };
      if (fleck || i < n) this.spots.push(spot);
    }
  }

  draw(t: number): void {
    const c = this.cfg;
    const g = this.g;
    g.clearRect(0, 0, this.w, this.h);
    const tp = (t / c.pulseMs) * Math.PI * 2;
    const ts = (t / c.swayMs) * Math.PI * 2;
    for (const s of this.spots) {
      const base = s.fleck ? c.fleckAlpha : c.alpha;
      const pulse = s.fleck ? c.pulse * 1.6 : c.pulse;
      g.globalAlpha = Math.max(0, Math.min(1, (base + pulse * Math.sin(tp + s.p)) * (s.gold ? 1 : c.greenAlpha)));
      const x = s.x + Math.sin(ts + s.p) * c.sway;
      const y = s.y + Math.cos(ts * 0.7 + s.p) * c.sway * 0.4;
      g.drawImage(s.gold ? this.gold : this.green, x - s.s, y - s.s, s.s * 2, s.s * 2);
    }
    g.globalAlpha = 1;
  }
}

/* ---------------------------------------------------------------- night */

/** White radial falloff, roughly Gaussian so stacked blobs never show a ring. Tinted per draw via a cached copy. */
function softSprite(rgb: RGB): HTMLCanvasElement {
  const n = 64;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
  const [r, gr, b] = rgb;
  for (const [at, a] of [[0, 1], [0.2, 0.86], [0.4, 0.56], [0.6, 0.27], [0.8, 0.08], [1, 0]] as const) {
    grd.addColorStop(at, `rgb(${r} ${gr} ${b} / ${a})`);
  }
  g.fillStyle = grd;
  g.fillRect(0, 0, n, n);
  return c;
}

/** A small tile of monochrome noise as a data URL, for a repeating CSS background. */
export function grainTile(size: number, alpha: number, seed = 3): string {
  // One noise pixel per device pixel (capped), so the grain stays fine on retina screens.
  size = Math.round(size * Math.min(window.devicePixelRatio || 1, 2));
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  if (!g) return '';
  const img = g.createImageData(size, size);
  const r = rng(seed);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = r() < 0.5 ? 0 : 255;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = Math.round(r() * alpha * 255);
  }
  g.putImageData(img, 0, 0);
  return c.toDataURL('image/png');
}

interface Drifter {
  sprite: HTMLCanvasElement;
  alpha: number;
  x: number;
  y: number;
  s: number;
  px: number;
  py: number;
  pa: number;
  fx: number;
  fy: number;
  moving: boolean;
}

/** Soft blurred colour fields on a dark ground, drifting very slowly, with a faint moon glow on top. */
export class Mist {
  private g: CanvasRenderingContext2D;
  private items: Drifter[] = [];
  private w = 0;
  private h = 0;
  private unit = 0;

  constructor(
    private canvas: HTMLCanvasElement,
    private cfg: BackdropConfig['night'] = BACKDROP.night,
  ) {
    this.g = canvas.getContext('2d', { alpha: true })!;
    const r = rng(cfg.seed);
    const make = (b: Blob, moving: boolean): Drifter => ({
      sprite: softSprite(b.color),
      alpha: b.alpha,
      x: b.x,
      y: b.y,
      s: b.r,
      px: r() * Math.PI * 2,
      py: r() * Math.PI * 2,
      pa: r() * Math.PI * 2,
      fx: (Math.PI * 2) / lerp(cfg.driftMs, r()),
      fy: (Math.PI * 2) / lerp(cfg.driftMs, r()),
      moving,
    });
    this.items = [...cfg.blobs.map((b) => make(b, true)), make(cfg.moon, false)];
  }

  resize(w: number, h: number): void {
    const c = this.cfg;
    this.w = w;
    this.h = h;
    this.unit = Math.sqrt(w * h);
    this.canvas.width = Math.max(1, Math.round(w * c.resolution));
    this.canvas.height = Math.max(1, Math.round(h * c.resolution));
    this.g.setTransform(c.resolution, 0, 0, c.resolution, 0, 0);
  }

  draw(t: number): void {
    const c = this.cfg;
    const g = this.g;
    g.clearRect(0, 0, this.w, this.h);
    const amp = c.drift * this.unit;
    for (const d of this.items) {
      const dx = d.moving ? Math.sin(t * d.fx + d.px) * amp : 0;
      const dy = d.moving ? Math.cos(t * d.fy + d.py) * amp * 0.7 : 0;
      const a = d.alpha * (1 + (d.moving ? c.breathe * Math.sin(t * d.fx * 1.3 + d.pa) : 0));
      const s = d.s * this.unit;
      g.globalAlpha = Math.max(0, Math.min(1, a));
      g.drawImage(d.sprite, d.x * this.w + dx - s, d.y * this.h + dy - s, s * 2, s * 2);
    }
    g.globalAlpha = 1;
  }
}

/* ----------------------------------------------------------- controller */

/**
 * Drives the two layers under `root` (.ambient-day / .ambient-night, each with a
 * canvas). Only visible scenes do work: a scene's loop runs while it is shown
 * (plus its fade-out), and nothing animates when the tab is hidden or the user
 * prefers reduced motion (the scenes are then painted once, static).
 */
export function mountBackdrop(root: HTMLElement, initial: Scene, cfg: BackdropConfig = BACKDROP): { setScene(s: Scene): void; destroy(): void } {
  const dayEl = root.querySelector<HTMLElement>('.ambient-day')!;
  const nightEl = root.querySelector<HTMLElement>('.ambient-night')!;
  dayEl.style.background = cfg.day.ground;
  nightEl.style.background = cfg.night.ground;
  root.style.setProperty('--ambient-fade', `${cfg.fadeMs}ms`);
  const grain = grainTile(cfg.night.grainTile, cfg.night.grain);
  if (grain) {
    nightEl.style.setProperty('--ambient-grain', `url(${grain})`);
    nightEl.style.setProperty('--ambient-grain-size', `${cfg.night.grainTile}px`);
  }
  const painters: Record<Scene, Dapple | Mist> = {
    day: new Dapple(dayEl.querySelector('canvas')!, cfg.day),
    night: new Mist(nightEl.querySelector('canvas')!, cfg.night),
  };
  const fps: Record<Scene, number> = { day: cfg.day.fps, night: cfg.night.fps };
  const still = matchMedia('(prefers-reduced-motion: reduce)');

  let scene = initial;
  let w = 0;
  let h = 0;
  let raf = 0;
  const last: Record<Scene, number> = { day: -Infinity, night: -Infinity };
  const until: Record<Scene, number> = { day: 0, night: 0 }; // keep animating a scene through its fade-out
  const t0 = performance.now();

  const visible = (s: Scene): boolean => scene === s || performance.now() < until[s];
  const paint = (s: Scene): void => painters[s].draw(performance.now() - t0);
  const frame = (now: number): void => {
    raf = 0;
    if (document.hidden || still.matches) return;
    let any = false;
    for (const s of ['day', 'night'] as const) {
      if (!visible(s)) continue;
      any = true;
      if (now - last[s] >= 1000 / fps[s] - 2) {
        last[s] = now;
        painters[s].draw(now - t0);
      }
    }
    if (any) raf = requestAnimationFrame(frame);
  };
  const run = (): void => {
    if (raf || document.hidden || still.matches) return;
    raf = requestAnimationFrame(frame);
  };
  const measure = (): void => {
    const nw = root.clientWidth;
    const nh = root.clientHeight;
    if (nw === w && nh === h) return;
    w = nw;
    h = nh;
    painters.day.resize(w, h);
    painters.night.resize(w, h);
    paint(scene);
  };

  measure();
  root.dataset.scene = scene;
  run();

  const ro = new ResizeObserver(measure);
  ro.observe(root);
  const onVis = (): void => run();
  document.addEventListener('visibilitychange', onVis);
  still.addEventListener('change', onVis);

  return {
    setScene(s) {
      if (s === scene) return;
      until[scene] = performance.now() + cfg.fadeMs + 50;
      paint(s);
      scene = s;
      root.dataset.scene = s;
      run();
    },
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      still.removeEventListener('change', onVis);
    },
  };
}
